import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Users, UserPlus, Phone, CheckCircle2, AlertCircle, TrendingUp, Clock, ArrowRight
} from "lucide-react";

export default function LeadOverview() {
  const { currentBranch } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, [currentBranch]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      const res = await api.get(`/lead-management/stats?${params}`);
      setStats(res.data);
    } catch (err) {
      console.error("Failed to fetch lead stats:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const statCards = [
    {
      label: "Total Leads",
      value: stats?.total || 0,
      icon: Users,
      color: "text-foreground",
      bgColor: "bg-secondary/50"
    },
    {
      label: "New Today",
      value: stats?.newToday || 0,
      icon: UserPlus,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10"
    },
    {
      label: "Pending Followups",
      value: stats?.pendingFollowups || 0,
      icon: Clock,
      color: "text-orange-500",
      bgColor: "bg-orange-500/10"
    },
    {
      label: "Converted",
      value: stats?.converted || 0,
      icon: CheckCircle2,
      color: "text-emerald-500",
      bgColor: "bg-emerald-500/10"
    },
  ];

  return (
    <div className="space-y-6" data-testid="lead-overview-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-3xl font-black font-heading tracking-tight uppercase">
            Lead Overview
          </h1>
          <p className="text-xs tracking-tight text-muted-foreground">
            Track your lead pipeline and conversion metrics
          </p>
        </div>
        <Button
          onClick={() => navigate("/leads/all")}
          className="font-heading uppercase tracking-wide text-xs"
          data-testid="view-all-leads-btn"
        >
          View All Leads
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label} className="bg-card border-border">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center shrink-0`}>
                    <Icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                  <div>
                    <p className={`text-2xl sm:text-3xl font-black font-heading ${stat.color}`}>
                      {stat.value}
                    </p>
                    <p className="text-[10px] sm:text-xs font-mono uppercase tracking-widest text-muted-foreground">
                      {stat.label}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Stage Breakdown */}
      {stats?.stageCounts && Object.keys(stats.stageCounts).length > 0 && (
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <h2 className="text-sm font-heading uppercase tracking-wide mb-4">
              Lead Stage Breakdown
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {Object.entries(stats.stageCounts).map(([stage, count]) => {
                const stageColors = {
                  "New": "border-blue-500/30 bg-blue-500/5 text-blue-500",
                  "Interested": "border-green-500/30 bg-green-500/5 text-green-500",
                  "Not interested": "border-zinc-500/30 bg-zinc-500/5 text-zinc-400",
                  "Converted": "border-emerald-500/30 bg-emerald-500/5 text-emerald-500",
                  "Lost": "border-red-500/30 bg-red-500/5 text-red-500",
                };
                return (
                  <div
                    key={stage}
                    className={`p-4 rounded-xl border ${stageColors[stage] || "border-border bg-secondary/30"}`}
                  >
                    <p className="text-xl font-black font-heading">{count}</p>
                    <p className="text-xs text-muted-foreground">{stage}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Breakdown */}
      {stats?.statusCounts && Object.keys(stats.statusCounts).length > 0 && (
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <h2 className="text-sm font-heading uppercase tracking-wide mb-4">
              Lead Status Breakdown
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {Object.entries(stats.statusCounts).map(([status, count]) => {
                const statusColors = {
                  "Not reachable": "border-orange-500/30 bg-orange-500/5 text-orange-500",
                  "RNR": "border-yellow-500/30 bg-yellow-500/5 text-yellow-500",
                  "Need followup": "border-purple-500/30 bg-purple-500/5 text-purple-500",
                };
                const displayName = status === "" ? "None" : status;
                const displayClass = statusColors[status] || "border-border bg-secondary/30 text-muted-foreground";
                return (
                  <div
                    key={status || "none"}
                    className={`p-4 rounded-xl border ${displayClass}`}
                  >
                    <p className="text-xl font-black font-heading">{count}</p>
                    <p className="text-xs text-muted-foreground">{displayName}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card
          className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer"
          onClick={() => navigate("/leads/all")}
          data-testid="quick-action-all-leads"
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="font-heading font-semibold">All Leads</h3>
              <p className="text-xs text-muted-foreground">View and manage all your leads</p>
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground ml-auto" />
          </CardContent>
        </Card>

        <Card
          className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer"
          onClick={() => navigate("/leads/followups")}
          data-testid="quick-action-followups"
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center">
              <Clock className="h-6 w-6 text-orange-500" />
            </div>
            <div>
              <h3 className="font-heading font-semibold">Followups Calendar</h3>
              <p className="text-xs text-muted-foreground">
                {stats?.overdue > 0 ? (
                  <span className="text-red-500">{stats.overdue} overdue followups</span>
                ) : (
                  "Schedule and track followups"
                )}
              </p>
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground ml-auto" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
